import {
    type QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { openingBalanceContract } from "@common/contracts/opening-balance.contract";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { getAccountsQueryKey } from "@hooks/useAccounts";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useScope } from "@state/scopeContext.lib";
import { accountingReportsQueryKeys } from "./accountingReports.api";

const openingBalanceContractClient = getBackendAPIClient(
    openingBalanceContract,
);

const openingBalanceQueryKeys = {
    all: () => ["openingBalance"],
    list: (entityId?: number, ledgerBasis?: AccountingLedgerBasis) => [
        ...openingBalanceQueryKeys.all(),
        "list",
        entityId,
        ledgerBasis,
    ],
    account: (
        entityId?: number,
        accountId?: string,
        ledgerBasis?: AccountingLedgerBasis,
    ) => [
        ...openingBalanceQueryKeys.all(),
        "account",
        entityId,
        accountId,
        ledgerBasis,
    ],
    unreconciledKickBalancesAll: () => [
        ...openingBalanceQueryKeys.all(),
        "unreconciledKickBalances",
    ],
    unreconciledKickBalances: (entityId?: number) => [
        ...openingBalanceQueryKeys.unreconciledKickBalancesAll(),
        entityId,
    ],
    unreconciledKickBalancesForEntities: (
        workspaceId: string | null | undefined,
        entityIds: number[],
    ) => [
        ...openingBalanceQueryKeys.unreconciledKickBalancesAll(),
        "forEntities",
        workspaceId,
        entityIds,
    ],
};

export function useOpeningBalancesQuery(
    entityId?: number,
    ledgerBasis = AccountingLedgerBasis.cash,
) {
    return useQuery({
        queryKey: openingBalanceQueryKeys.list(entityId, ledgerBasis),
        queryFn: async () => {
            if (!entityId) {
                return null;
            }
            const response =
                await openingBalanceContractClient.getOpeningBalances({
                    params: { entityId },
                    query: { ledgerBasis },
                });
            return response.body;
        },
    });
}

export function useOpeningBalance(
    entityId?: number,
    accountId?: string,
    ledgerBasis = AccountingLedgerBasis.cash,
) {
    return useQuery({
        queryKey: openingBalanceQueryKeys.account(
            entityId,
            accountId,
            ledgerBasis,
        ),
        queryFn: async () => {
            if (!entityId || !accountId) {
                return null;
            }
            const response =
                await openingBalanceContractClient.getOpeningBalance({
                    params: { entityId, accountId },
                    query: { ledgerBasis },
                });
            return response.body;
        },
        enabled: !!entityId && !!accountId,
        gcTime: 0,
    });
}

export function useSetOpeningBalancesBulkMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            params,
            query,
            body,
        }: ServerInferRequest<
            typeof openingBalanceContract.setOpeningBalancesBulk
        >) => {
            await openingBalanceContractClient.setOpeningBalancesBulk({
                params,
                query,
                body,
            });
        },
        onSuccess: async (_, { params, query }) => {
            invalidateOpeningBalanceRelatedQueries(
                queryClient,
                params.entityId,
            );
            queryClient.invalidateQueries({
                queryKey: openingBalanceQueryKeys.list(
                    params.entityId,
                    query?.ledgerBasis,
                ),
            });
        },
    });
}

export function useUpsertOpeningBalanceMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            params,
            query,
            body,
        }: ServerInferRequest<
            typeof openingBalanceContract.upsertOpeningBalance
        >) => {
            const response =
                await openingBalanceContractClient.upsertOpeningBalance({
                    params,
                    query,
                    body,
                });
            return response.body;
        },
        onSuccess: async (_, { params, query }) => {
            invalidateOpeningBalanceRelatedQueries(
                queryClient,
                params.entityId,
            );
            queryClient.invalidateQueries({
                queryKey: openingBalanceQueryKeys.list(
                    params.entityId,
                    query?.ledgerBasis,
                ),
            });
            queryClient.invalidateQueries({
                queryKey: openingBalanceQueryKeys.account(
                    params.entityId,
                    params.accountId,
                    query?.ledgerBasis,
                ),
            });
        },
    });
}

export function useRemoveOpeningBalanceMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            entityId,
            accountId,
            ledgerBasis = AccountingLedgerBasis.cash,
        }: {
            entityId: number;
            accountId: string;
            ledgerBasis?: AccountingLedgerBasis;
        }) => {
            await openingBalanceContractClient.removeOpeningBalance({
                params: { entityId, accountId },
                query: { ledgerBasis },
            });
        },
        onSuccess: async (_, { entityId, accountId, ledgerBasis }) => {
            invalidateOpeningBalanceRelatedQueries(queryClient, entityId);
            queryClient.invalidateQueries({
                queryKey: openingBalanceQueryKeys.list(entityId, ledgerBasis),
            });
            queryClient.invalidateQueries({
                queryKey: openingBalanceQueryKeys.account(
                    entityId,
                    accountId,
                    ledgerBasis,
                ),
            });
        },
    });
}

export function useUnreconciledKickBalancesForEntities(entityIds: number[]) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: openingBalanceQueryKeys.unreconciledKickBalancesForEntities(
            workspaceId,
            entityIds,
        ),
        queryFn: async () => {
            if (!workspaceId || entityIds.length === 0) {
                return [];
            }
            const response =
                await openingBalanceContractClient.getUnreconciledKickBalancesForEntities(
                    {
                        params: { workspaceId },
                        query: { entityIds },
                    },
                );
            return response.body;
        },
        enabled: !!workspaceId && entityIds.length > 0,
    });
}

function invalidateOpeningBalanceRelatedQueries(
    queryClient: QueryClient,
    entityId: number,
) {
    queryClient.invalidateQueries({
        queryKey: [accountingReportsQueryKeys.chartOfAccounts],
    });
    queryClient.invalidateQueries({
        queryKey: getAccountsQueryKey(entityId),
    });
    queryClient.invalidateQueries({
        queryKey: openingBalanceQueryKeys.unreconciledKickBalancesAll(),
    });
}
